import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { getQueryString, useRouter } from '@framework/router';
import { getBreadcrumbs, getLeagueTableItemRoute } from './helpers';
import { TOP_COMPANIES_TABLE_COLUMNS } from './constants';

import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';

import SearchInput from './LeagueSearchInput';
import TTMSelect from './TTMSelect';

import TopUnderwriters from './Underwriters/TopUnderwriters';
import TopUnderwritersTTMSelect from './Underwriters/TopUnderwriters/TTMSelect';

import TopRatingAgencies from './RatingAgencies/TopRatingAgencies';
import TopRatingAgenciesTTMSelect from './RatingAgencies/TopRatingAgencies/TTMSelect';

import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema/MetaSchema';
import { useBreadcrumbs } from '@/creditflowresearch/ui/common/hooks';
import { useLeagueTableContext } from '@/creditflowresearch/application/League/Table/LeagueTableContext';
import { useMetaTagsContext } from '@/creditflowresearch/application/MetaTags/MetaTagsContext';
import metaTags from '@/creditflowresearch/ui/common/metaTagsConstants';
import { initProductRegionFilters } from '@/creditflowresearch/application/PageFiltersState/productRegionFilters/initProductRegionFilters';
import { usePageFiltersStateContext } from '@/creditflowresearch/application/PageFiltersState/PageFiltersStateContext';
import LeagueTableTabContent from '@/creditflowresearch/ui/components/league/LeagueTablePage/LeagueTableTabContent';
import { useLeagueTableFiltersContext } from '@/creditflowresearch/application/League/Table/LeagueTableFiltersContext';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { LeagueTablesNoAccessComponent } from '@/creditflowresearch/ui/components/league/LeagueTablePage/LeagueTablesNoAccess';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import BetaTitle from '@/creditflowresearch/ui/common/components/Title/BetaTitle';
import { Product, CorporateProduct } from '@/creditflowresearch/domain/Product';
import { AbsProduct } from '@/creditflowresearch/domain/Product';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import { Tile, TileHeader } from '@creditflowresearch/uilib/Tile';
import Title from '@creditflowresearch/uilib/Title';
import Flex from '@creditflowresearch/uilib/Flex';
import LeagueTableTileHeader from './LeagueTableTileHeader';
import LeagueTableTabSelect from './LeagueTableTabSelect';

import styles from '@/creditflowresearch/ui/components/league/LeagueTablePage/leagueTablePage.module.scss';
import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

const LeagueTablePage = () => {
  const panelBackground = useProductGradient();
  const pageFiltersState = usePageFiltersStateContext();
  const { setMetaTags } = useMetaTagsContext();
  const { query } = useRouter();
  const { products, regions, q } = query;
  const search = { products, regions, q };
  const didMount = useRef(false);
  const {
    state: leagueTableState,
    getCompaniesAllByFilter,
    getRatingAgenciesByFilter,
    getUnderwritersByFilter,
    paginateCompaniesAll,
    paginateRatingAgencies,
    paginateUnderwriters,
    resetCompaniesAll,
    resetUnderwriters,
    resetRatingAgencies,
  } = useLeagueTableContext();
  const { state: filtersState } = useLeagueTableFiltersContext();
  const [initialProductsRegionsFilters, setInitialProductsRegionsFilters] = useState({});
  const isPageLoading = leagueTableState.underwritersCollection.isFetching
    || leagueTableState.topUnderwritersCollection.isFetching
    || leagueTableState.ratingAgenciesCollection.isFetching
    || leagueTableState.topRatingAgenciesCollection.isFetching
    || leagueTableState.companiesAllCollection.isFetching;

  const { checkAccessByUserPlan } = useUserPlanContext();
  const {
    checkAccessByUserRegions,
    checkAccessByUserProducts,
    pageFilters,
    includedProductIds,
    regionFilters,
    corporateProductFilters,
  } = usePageFiltersContext();
  const granted = useAccessGrant();
  // TODO: remove when the European market opening period ends
  const checks = [
    granted || checkAccessByUserPlan(UserPlanType.PREMIUM),
    granted || checkAccessByUserRegions(),
    granted || checkAccessByUserProducts(),
  ];

  const selectedPeriod = filtersState.periods.leagueTable.selected;

  const [isPagesReset, setIsPagesReset] = useState(false);

  const {
    isHeaderInitialized,
    products: productsList,
    regions: regionsList,
  } = useHeaderMenuContext();

  useEffect(() => {
    if (isHeaderInitialized) {
      setMetaTags(metaTags.leagueTable());
    }
  }, [isHeaderInitialized]);

  useEffect(() => {
    if (isHeaderInitialized) {
      initProductRegionFilters(
        productsList,
        regionsList,
        true,
        query,
        pageFiltersState,
      );
    }
  }, [products, regions, isHeaderInitialized, query]);

  useEffect(() => {
    setInitialProductsRegionsFilters(search);

    return () => {
      resetCompaniesAll();
      resetUnderwriters();
      resetRatingAgencies();
    };
  }, []);

  useEffect(() => {
    if (didMount.current) {
      paginateCompaniesAll(1);
      paginateRatingAgencies(1);
      paginateUnderwriters(1);
      setIsPagesReset(true);
    } else {
      didMount.current = true;
    }
  }, [q, selectedPeriod, products, regions]);

  useEffect(() => {
    if (isPagesReset) {
      getCompaniesAllByFilter();
      getRatingAgenciesByFilter();
      getUnderwritersByFilter();
      setIsPagesReset(false);
    }
  }, [isPagesReset]);

  useBreadcrumbs(
    () => getBreadcrumbs(initialProductsRegionsFilters),
    [initialProductsRegionsFilters],
  );

  const topCompaniesTablesCommonProps = {
    rowClassName: styles.tableRow,
    tableClassName: styles.topItemsTable,
    emptyMessageClassName: styles.topItemsEmptyMessage,
    columns: TOP_COMPANIES_TABLE_COLUMNS,
    getRoutePath: getLeagueTableItemRoute(getQueryString(search)),
    showColumnsHeaders: false,
    isItemsPerPageVisible: false,
  };

  const renderContent = () => (
    <>
      <div className={ styles.row }>
        <Tile dataTest="topUnderwriters" className={ styles.topItemsTableContainer }>
          <TileHeader title={ <Title title="Top Underwriters" /> } dataTest="topUnderwritersHeader">
            <TopUnderwritersTTMSelect />
          </TileHeader>
          <TopUnderwriters { ...topCompaniesTablesCommonProps } />
        </Tile>
        <Tile dataTest="topRatingAgencies" className={ styles.topItemsTableContainer }>
          <TileHeader title={ <Title title="Top Rating Agencies" /> } dataTest="topRatingAgenciesHeader">
            <TopRatingAgenciesTTMSelect />
          </TileHeader>
          <TopRatingAgencies { ...topCompaniesTablesCommonProps } />
        </Tile>
      </div>
      <Tile dataTest="leagueTable" className={ spacesStyles.mbn }>
        <LeagueTableTileHeader>
          <Flex
            direction={ { sm: 'column-reverse', md: 'column-reverse', xl: 'row' } }
            align={ { sm: 'stretch', md: 'stretch', xl: 'center' } }
            justify={ { xl: 'flex-end' } }
            gap={ 10 }
          >
            <SearchInput />
            <div className={ styles.mobileTabSelect }>
              <LeagueTableTabSelect />
            </div>
            <TTMSelect />
          </Flex>
        </LeagueTableTileHeader>
        <LeagueTableTabContent />
      </Tile>
    </>
  );

  return (
    <>
      <MetaSchema />
      <ProductPageHeader
        title={ (
          <BetaTitle
            title="Fixed Income Underwriter & Rating Agency League Tables"
            showBeta={ !Product.areAllOfType(pageFilters.productIds, AbsProduct.TYPE) }
            className={ styles.title }
          />
        ) }
        items={ [
          {
            key: 'regions',
            items: regionFilters,
          },
          {
            key: 'products',
            items: corporateProductFilters,
            isVisible: Product.areAllOfTypes(includedProductIds, CorporateProduct.CORPORATE_TYPES),
          },
        ] }
        accessConditions={ [{ checks }] }
        // TODO: remove when the European market opening period ends
        blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        isDisabled={ isPageLoading }
        panelBackground={ panelBackground }
      />
      <CheckUserAccess conditions={ [{ checks, fallback: LeagueTablesNoAccessComponent }] }>
        { renderContent() }
      </CheckUserAccess>
      <MissingSomethingModalButton className={ cn(spacesStyles.ptxl, spacesStyles.pbxl) } />
    </>
  );
};

export default LeagueTablePage;
